import React, { useEffect, useState } from 'react';
import { Button, Card, Checkbox, Drawer, Form, Input, Modal, Popconfirm, Space } from 'antd';
import { DownOutlined, EditOutlined, RobotOutlined } from '@ant-design/icons';
import InputNumber from 'components/input-number';
import ImagePicker from 'components/pickers/image/MultiImagePicker';
import MultiYoutubeVideoPicker from 'components/pickers/youtube-video/MultiYoutubeVideoPicker';
import TagsPicker from 'components/pickers/product_tag';
import CategoryPicker from 'components/pickers/product_category';
import useVariantComponent from '../variant/useComponent';
import VariantBlockMobile from './components/VariantBlockMobile';
import usePriceComponent from '../price/useComponent';
import PriceBlockMobile from './components/PriceBlockMobile';
import { useWorkSpace } from 'contexts/workspace';
import { useApp } from 'contexts/app';

const InventoryRowMobileEdit = ({ row, showTitle, onChange, onChangeAll }) => {
  const [open, setOpen] = useState(false);
  const [qty, setQty] = useState(0);

  useEffect(() => {
    setQty(row?.on_hand_quantity || 0);
  }, [row?.on_hand_quantity]);

  const handleOk = () => {
    setOpen(false);
    onChange(qty);
  };

  const handleApplyAll = () => {
    setOpen(false);
    onChangeAll(qty);
  };

  return (
    <Card size='small' type='inner'>
      <Space direction='vertical' size={4} style={{ width: '100%' }}>
        {showTitle && <div style={{ fontWeight: 500 }}>{row.variant_title}</div>}
        <div>Tồn: <b>{row.on_hand_quantity}</b></div>
        <Button type='text' size='small' icon={<EditOutlined />} onClick={() => setOpen(true)} />
      </Space>

      <Modal
        title='Cập nhật tồn kho'
        open={open}
        onCancel={() => setOpen(false)}
        footer={
          <Space>
            {onChangeAll && <Button onClick={handleApplyAll}>Áp dụng chung</Button>}
            <Button type='primary' onClick={handleOk}>Áp dụng</Button>
            <Button onClick={() => setOpen(false)}>Hủy</Button>
          </Space>
        }
      >
        <Space direction='vertical' size={8}>
          {showTitle && <div><b>{row.variant_title}</b></div>}
          <div>Số lượng tồn kho:</div>
          <InputNumber value={qty} onChange={(v) => setQty(v)} min={0} />
        </Space>
      </Modal>
    </Card>
  );
};

const Mobile = ({ viewModel }) => {
  const { workspace } = useWorkSpace();
  const { message } = useApp();
  const {
    id,
    form,
    inventoryRows,
    draftOptions,
    draftVariants,
    saving,
    showAdvanced,
    setShowAdvanced,
    onFinish,
    deleteProduct,
    copyProduct,
    onDraftVariantStateChange,
    onDraftPriceChange,
    onVariantStateChange,
    onPriceStateChange,
    onInventoryChange,
    onInventoryChangeAll,
    generatingDescription,
    generateDescriptionByAI,
  } = viewModel;
  const variantViewModel = useVariantComponent({
    id,
    draftMode: !id,
    draftOptions,
    draftVariants,
    onDraftChange: onDraftVariantStateChange,
    onStateChange: onVariantStateChange,
  });
  const priceViewModel = usePriceComponent({
    id,
    draftMode: !id,
    draftVariants,
    onDraftVariantsChange: onDraftPriceChange,
    onStateChange: onPriceStateChange,
  });
  const [openPriceSourcePicker, setOpenPriceSourcePicker] = useState(false);

  const handleViewProductWebsite = () => {
    const productID = String(id || '').trim();
    if (!productID) {
      message.error('Sản phẩm chưa được tạo để xem trên website');
      return;
    }

    const websiteBaseUrl = String(workspace?.websiteUrl || '').replace(/\/$/, '');
    if (!websiteBaseUrl) {
      message.error('Chưa cấu hình website shop để xem sản phẩm');
      return;
    }

    window.open(`${websiteBaseUrl}/product/${encodeURIComponent(productID)}`, '_blank', 'noopener,noreferrer');
  };

  const selectedSourceLabel = (priceViewModel?.sourceOptions || []).find((x) => x?.value === priceViewModel?.selectedSourceKey)?.label;
  const priceTableCount = Math.max(0, (priceViewModel?.sourceOptions || []).length - 1);
  const hasMultiplePriceTables = priceTableCount > 1;

  const priceCardTitle = (
    <Space direction='vertical' size={8} style={{ width: '100%' }}>
      {!(priceViewModel?.pricingEnabled && hasMultiplePriceTables) && <div style={{ fontWeight: 600 }}>Giá</div>}
      {priceViewModel?.pricingEnabled && (
        <div>
          <Button
            type='text'
            style={{ padding: 0, fontWeight: 500, height: 'auto' }}
            onClick={() => setOpenPriceSourcePicker(true)}
            icon={<DownOutlined style={{ fontSize: 12 }} />}
            iconPosition='end'
          >
            {`Giá: ${selectedSourceLabel || 'Giá sản phẩm gốc'}`}
          </Button>
        </div>
      )}
    </Space>
  );

  const infoContent = (
    <Space direction='vertical' size={12} style={{ width: '100%' }}>
      <Card size='small' title='Thông tin cơ bản'>
        <Form.Item hidden name='id' />
        <Form.Item label='Tên sản phẩm' name='name' rules={[{ required: true, message: 'Vui lòng nhập tên sản phẩm' }]}>
          <Input placeholder='Ví dụ: Trà đào cam sả' />
        </Form.Item>
        <Form.Item label='Hình ảnh' name='images' rules={[{ required: true, message: 'Vui lòng thêm hình ảnh' }]}>
          <ImagePicker />
        </Form.Item>
        <Form.Item label='Danh mục' name='category'>
          <CategoryPicker />
        </Form.Item>
        <Form.Item label='Tags' name='tags'>
          <TagsPicker mode='multiple' />
        </Form.Item>
        <Form.Item label='Mô tả' name='description' rules={[{ required: true, message: 'Vui lòng nhập mô tả sản phẩm' }]}>
          <Input.TextArea rows={4} />
        </Form.Item>
        <Space style={{ marginTop: -8, marginBottom: 12 }}>
          <Button icon={<RobotOutlined />} loading={generatingDescription} onClick={generateDescriptionByAI}>
            Tạo mô tả bằng AI
          </Button>
        </Space>
        <Form.Item name='show_on_website' valuePropName='checked' style={{ marginBottom: 0 }}>
          <Checkbox>Hiển thị trên website</Checkbox>
        </Form.Item>
      </Card>
    </Space>
  );

  const setupContent = null;

  return (
    <Form
      form={form}
      layout='vertical'
      initialValues={{
        track_inventory: false,
        allow_backorder: true,
      }}
      onFinish={onFinish}
      autoComplete='off'
    >
      <Space direction='vertical' size={12} style={{ width: '100%', paddingBottom: 88 }}>
        {infoContent}
        {setupContent}

        <Card size='small' title='Biến thể'>
          <VariantBlockMobile viewModel={variantViewModel} />
        </Card>

        <Card size='small' title={priceCardTitle}>
          <PriceBlockMobile viewModel={priceViewModel} hasMultipleVariants={variantViewModel.hasMultipleVariants} />
        </Card>

        <Card size='small' title='Kho'>
          <Form.Item name='track_inventory' valuePropName='checked'>
            <Checkbox>Bật theo dõi tồn kho</Checkbox>
          </Form.Item>
          <Form.Item name='allow_backorder' valuePropName='checked'>
            <Checkbox>Cho phép đặt vượt tồn</Checkbox>
          </Form.Item>
          {id || draftVariants.length > 0 ? (
            <Space direction='vertical' size={8} style={{ width: '100%' }}>
              {inventoryRows.map((row) => (
                <InventoryRowMobileEdit
                  key={row.id || row.variant_id}
                  row={row}
                  showTitle={variantViewModel.hasMultipleVariants}
                  onChange={(qty) => onInventoryChange(row.id || row.variant_id, qty)}
                  onChangeAll={variantViewModel.hasMultipleVariants ? onInventoryChangeAll : undefined}
                />
              ))}
              {inventoryRows.length === 0 && <div style={{ color: '#8c8c8c' }}>Chưa có dữ liệu tồn kho.</div>}
            </Space>
          ) : (
            <div style={{ color: '#8c8c8c' }}>Tạo biến thể để hiển thị dữ liệu kho.</div>
          )}
        </Card>

        <Card
          size='small'
          title='Thông tin mở rộng'
          extra={<Button type='link' onClick={() => setShowAdvanced(!showAdvanced)}>{showAdvanced ? 'Thu gọn' : 'Mở rộng'}</Button>}
        >
          {showAdvanced ? (
            <Space direction='vertical' size={0} style={{ width: '100%' }}>
              <Form.Item label='Youtube videos' name='videos'>
                <MultiYoutubeVideoPicker />
              </Form.Item>
              <Form.Item label='Shopee link' name='shopee_link'>
                <Input />
              </Form.Item>
              <Form.Item label='Tiktok shop link' name='tiktok_shop_link'>
                <Input />
              </Form.Item>
            </Space>
          ) : (
            <div style={{ color: '#8c8c8c' }}>Ẩn bớt các trường ít dùng để màn hình gọn và tập trung hơn.</div>
          )}
        </Card>
        {id && (
          <Space direction='vertical' size={8} style={{ width: '100%' }}>
            <Button block onClick={handleViewProductWebsite}>Xem trên website</Button>
            <Popconfirm title='Copy sản phẩm' onConfirm={copyProduct}>
              <Button block>Copy sản phẩm</Button>
            </Popconfirm>
            <Popconfirm title='Xoá sản phẩm' onConfirm={deleteProduct}>
              <Button block danger>Xoá sản phẩm</Button>
            </Popconfirm>
          </Space>
        )}
      </Space>

      <div
        style={{
          position: 'fixed',
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 1000,
          background: '#fff',
          borderTop: '1px solid #f0f0f0',
          padding: '10px 12px calc(10px + env(safe-area-inset-bottom, 0px))',
          boxShadow: '0 -4px 12px rgba(0, 0, 0, 0.06)',
        }}
      >
        <Button type='primary' htmlType='submit' block loading={saving}>
          {id ? 'Lưu thay đổi' : 'Tạo sản phẩm'}
        </Button>
      </div>

      <Drawer
        title='Chọn nguồn giá'
        placement='bottom'
        height='60vh'
        open={openPriceSourcePicker}
        onClose={() => setOpenPriceSourcePicker(false)}
      >
        <Space direction='vertical' size={8} style={{ width: '100%' }}>
          {(priceViewModel?.sourceOptions || []).map((item) => (
            <Button
              key={item?.value}
              block
              size='large'
              type={item?.value === priceViewModel?.selectedSourceKey ? 'primary' : 'default'}
              onClick={() => {
                priceViewModel?.setSelectedSourceKey?.(item?.value);
                setOpenPriceSourcePicker(false);
              }}
            >
              {item?.label}
            </Button>
          ))}
        </Space>
      </Drawer>
    </Form>
  );
};

export default Mobile;